import React, { useState, useEffect, useRef } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import AdminConsultations from '../components/admin/AdminConsultations';
import AdminBlogPosts from '../components/admin/AdminBlogPosts';
import AdminQuizzes from '../components/admin/AdminQuizzes';
import AdminDashboard from '../components/admin/AdminDashboard';
import AdminNewsletter from '../components/admin/AdminNewsletter';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';

// Define the tab options for reuse
const tabOptions = [
  { id: 'dashboard', label: 'Oversigt' },
  { id: 'consultations', label: 'Konsultationer' },
  { id: 'blog', label: 'Blog Indlæg' },
  { id: 'quizzes', label: 'Quizzer' },
  { id: 'newsletter', label: 'Nyhedsbrev' }
];

const AdminDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const location = useLocation();
  const navigate = useNavigate();
  const blogPostsRef = useRef<any>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Check for URL parameters on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editPostId = searchParams.get('edit');
    
    if (editPostId) {
      // Switch to blog tab if edit parameter is present
      setActiveTab('blog');
      
      // Store the post ID to be edited
      if (blogPostsRef.current) {
        blogPostsRef.current.editPostById(editPostId);
      }
      
      // Clear the URL parameter after processing
      navigate('/admin', { replace: true });
    }
  }, [location, navigate]);

  // Scroll active tab into view when it changes
  useEffect(() => {
    if (sliderRef.current) {
      const activeElement = sliderRef.current.querySelector(`[data-value="${activeTab}"]`);
      if (activeElement) {
        // Calculate the center position
        const containerWidth = sliderRef.current.offsetWidth;
        const elementWidth = activeElement.clientWidth;
        const elementLeft = (activeElement as HTMLElement).offsetLeft;
        
        // Scroll to center the active tab
        sliderRef.current.scrollTo({
          left: elementLeft - (containerWidth / 2) + (elementWidth / 2),
          behavior: 'smooth'
        });
      }
    }
    
    // Close mobile menu when tab changes
    setShowMobileMenu(false);
  }, [activeTab]);

  // Handle touch events for swipe functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe || isRightSwipe) {
      const currentIndex = tabOptions.findIndex(tab => tab.id === activeTab);
      
      if (isLeftSwipe && currentIndex < tabOptions.length - 1) {
        // Swipe left to go to next tab
        setActiveTab(tabOptions[currentIndex + 1].id);
      } else if (isRightSwipe && currentIndex > 0) {
        // Swipe right to go to previous tab
        setActiveTab(tabOptions[currentIndex - 1].id);
      }
    }
    
    // Reset touch positions
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Navigate to next or previous tab
  const navigateTab = (direction: 'next' | 'prev') => {
    const currentIndex = tabOptions.findIndex(tab => tab.id === activeTab);
    
    if (direction === 'next' && currentIndex < tabOptions.length - 1) {
      setActiveTab(tabOptions[currentIndex + 1].id);
    } else if (direction === 'prev' && currentIndex > 0) {
      setActiveTab(tabOptions[currentIndex - 1].id);
    }
  };

  if (!user) {
    return (
      <div className="container-custom py-12 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-snudefryd-blue"></div>
      </div>
    );
  }

  if (!user.isAdmin) {
    return (
      <div className="container-custom py-12">
        <h1 className="text-2xl font-bold text-red-500">Adgang nægtet</h1>
        <p>Du har ikke adgang til denne side.</p>
      </div>
    );
  }

  return (
    <div className="container-custom py-4 md:py-8 px-3 md:px-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-snudefryd-blue">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">
            Velkommen, {user.username}. 
          </p>
        </div>
        <div className="mt-3 md:mt-0">
          <div className="bg-snudefryd-light-purple/10 px-3 py-1.5 md:px-4 md:py-2 rounded-md">
            <p className="text-xs md:text-sm text-snudefryd-purple">
              <span className="font-medium">Sidste login:</span> {new Date().toLocaleDateString('da-DK')}
            </p>
          </div>
        </div>
      </div>

      <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Mobile navigation controls */}
        <div className="md:hidden mb-4">
          <div className="flex items-center justify-between">
            {/* Previous button */}
            <button 
              onClick={() => navigateTab('prev')}
              disabled={tabOptions.findIndex(tab => tab.id === activeTab) === 0}
              className="p-2 rounded-md bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Forrige fane"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            
            {/* Current tab dropdown */}
            <div className="relative">
              <button 
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="flex items-center justify-between w-48 px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
              >
                <span className="font-medium text-sm">
                  {tabOptions.find(tab => tab.id === activeTab)?.label}
                </span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className={`transition-transform ${showMobileMenu ? 'rotate-180' : ''}`}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              
              {/* Dropdown menu */}
              {showMobileMenu && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                  <ul className="py-1">
                    {tabOptions.map(tab => (
                      <li key={tab.id}>
                        <button
                          onClick={() => setActiveTab(tab.id)}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            activeTab === tab.id 
                              ? 'bg-snudefryd-blue/10 text-snudefryd-blue font-medium' 
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          {tab.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            {/* Next button */}
            <button 
              onClick={() => navigateTab('next')}
              disabled={tabOptions.findIndex(tab => tab.id === activeTab) === tabOptions.length - 1}
              className="p-2 rounded-md bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Næste fane"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Desktop tabs */}
        <div 
          className="relative mb-6 -mx-3 px-3 md:mx-0 md:px-0 hidden md:block"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div 
            ref={sliderRef}
            className="overflow-x-auto hide-scrollbar pb-1"
          >
            <TabsList className="w-max min-w-full flex p-1 h-auto">
              {tabOptions.map(tab => (
                <TabsTrigger 
                  key={tab.id}
                  value={tab.id} 
                  data-value={tab.id}
                  className="flex-1 py-3 px-3 md:px-5 text-sm whitespace-nowrap rounded-md"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>
        
        <TabsContent value="dashboard" className="space-y-4">
          <AdminDashboard />
        </TabsContent>
        
        <TabsContent value="consultations" className="space-y-4">
          <AdminConsultations />
        </TabsContent>
        
        <TabsContent value="blog" className="space-y-4">
          <AdminBlogPosts ref={blogPostsRef} />
        </TabsContent>
        
        <TabsContent value="quizzes" className="space-y-4">
          <AdminQuizzes />
        </TabsContent>
        
        <TabsContent value="newsletter" className="space-y-4">
          <AdminNewsletter />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboardPage; 